
import { defineComponent } from 'vue'
import { FileTextIcon } from '@zhuowenli/vue-feather-icons'
import { store } from '@/store'
import { getMyGroups } from '@training24/organisation/src/group'
import type { Group } from '@training24/organisation/src/group'
import type { LmsModule, CourseLesson } from '@/store/lmsCourse/index'
import type { CourseAssignment } from '@/store/courseAssignment/index'
import type { Instruction, InstructionConfirmation } from '@/store/instruction'

interface InstructionEntry {
  instruction: Instruction
  deadline: string | null
  confirmed: boolean
}

interface CourseItem {
  courseId: string
  deadline: string | null
  module: LmsModule | null
}

interface GroupWithCourses {
  groupId: string
  name: string
  courses: CourseItem[]
}

export default defineComponent({
  name: 'LmsCatalogView',
  components: { FileTextIcon },

  data() {
    return {
      loading: true,
      myGroups: [] as Group[],
    }
  },

  computed: {
    userId(): string {
      return store.getters['getUserId'] ?? 'anon'
    },
    orgId(): string {
      return store.getters['organisation/getOrganisationId']
    },
    jwt(): string {
      return store.getters['getJWT']
    },
    /** Vorname für die Begrüßung in der Intro */
    firstName(): string {
      return store.getters.getUser?.first_name ?? ''
    },
    /** Name der aktiven Organisation für die Intro-Zeile */
    organisationName(): string {
      return store.getters['organisation/getOrganisation']?.name ?? ''
    },
    /** true, sobald JWT und Organisation geladen sind (für Re-Init beim ersten Landen) */
    ready(): boolean {
      return !!(store.getters['getJWT'] && store.getters['organisation/getOrganisationId'])
    },
    allAssignments(): CourseAssignment[] {
      return store.getters['courseAssignment/isAllLoaded']
        ? (store.state as any).courseAssignment.all
        : []
    },

    /** Alle Kurse des Nutzers – egal ob direkt oder über eine Gruppe zugewiesen,
     *  zusammengeführt und nach Kurs dedupliziert. */
    allCourseItems(): CourseItem[] {
      const groupIds = new Set(this.myGroups.map((g) => g.groupId))
      const assignments = this.allAssignments.filter(
        (a) =>
          (a as any).kind !== 'INSTRUCTION' &&
          ((a.targetType === 'USER' && a.targetId === this.userId) ||
            (a.targetType === 'GROUP' && groupIds.has(a.targetId)))
      )
      return this.assignmentsToCourseItems(assignments)
    },

    allInstructions(): Instruction[] {
      return store.getters['instruction/getAll'] || []
    },

    myConfirmations(): InstructionConfirmation[] {
      return (store.state as any).instruction?.myConfirmations || []
    },

    /** Eigene Unterweisungen, die dem Nutzer (direkt oder über seine Gruppen) zugewiesen sind.
     *  Quelle sind jetzt die vereinheitlichten Zuweisungen (kind === 'INSTRUCTION'). */
    myInstructions(): InstructionEntry[] {
      const groupIds = new Set(this.myGroups.map((g) => g.groupId))
      const insById = new Map<string, Instruction>(
        this.allInstructions.map((i) => [i.instructionId, i])
      )
      const seen = new Set<string>()
      const result: InstructionEntry[] = []
      for (const a of this.allAssignments) {
        if ((a as any).kind !== 'INSTRUCTION') continue
        const matches =
          (a.targetType === 'USER' && a.targetId === this.userId) ||
          (a.targetType === 'GROUP' && groupIds.has(a.targetId))
        if (!matches) continue
        const insId = (a as any).instructionId
        const ins = insId ? insById.get(insId) : undefined
        if (!ins || seen.has(insId)) continue
        seen.add(insId)
        result.push({
          instruction: ins,
          deadline: a.deadline || null,
          confirmed: this.myConfirmations.some((c) => c.instructionId === insId),
        })
      }
      return result
    },
  },

  methods: {
    assignmentsToCourseItems(assignments: CourseAssignment[]): CourseItem[] {
      // Deduplicate by courseId (keep earliest deadline or null)
      const map = new Map<string, CourseItem>()
      for (const a of assignments) {
        if (!map.has(a.courseId)) {
          map.set(a.courseId, {
            courseId: a.courseId,
            deadline: a.deadline ?? null,
            module: store.getters['lmsCourse/getModuleById'](a.courseId),
          })
        }
      }
      // Only show courses that actually exist in the catalog
      return Array.from(map.values()).filter((c) => c.module !== null)
    },

    isCompleted(moduleId: string): boolean {
      return store.getters['lmsCourse/isCompleted'](moduleId)
    },

    /** Kurs wurde begonnen (Lektion abgeschlossen oder mittendrin), aber noch nicht abgeschlossen */
    isInProgress(moduleId: string): boolean {
      if (this.isCompleted(moduleId)) return false
      return store.getters['lmsCourse/completedLessonCount'](moduleId) > 0
        || store.getters['lmsCourse/inProgressLessonCount'](moduleId) > 0
    },

    /** Gesamtfortschritt in % – gleiche Rechnung wie auf der Modulseite (overallPercent) */
    progressPercent(moduleId: string): number {
      const lessons: CourseLesson[] = store.getters['lmsCourse/getLessons'](moduleId)
      if (lessons.length === 0) return 0
      const sum = lessons.reduce(
        (acc: number, l: CourseLesson) =>
          acc + store.getters['lmsCourse/lessonProgress'](moduleId, l.lessonId),
        0,
      )
      return Math.round((sum / lessons.length) * 100)
    },

    formatDate(iso: string): string {
      return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },

    isOverdue(iso: string): boolean {
      return new Date(iso) < new Date()
    },

    openModule(moduleId: string) {
      this.$router.push({ name: 'lmsModule', params: { moduleId } })
    },

    openInstruction(instructionId: string) {
      this.$router.push({ name: 'lmsInstruction', params: { instructionId } })
    },

    // Unterzeile der Karte: Kategorie bzw. Dokumentanzahl — nie der rohe Dateiname.
    instructionDesc(ins: Instruction): string {
      if (ins.category) return ins.category
      const n = ins.sections && ins.sections.length ? ins.sections.length : 1
      return `${n} Dokument${n > 1 ? 'e' : ''}`
    },

    async init() {
      // Ohne geladene Organisation noch keine Zuweisungen abrufbar – der Watcher
      // auf `ready` ruft init() erneut, sobald die Org da ist (Spinner bleibt).
      if (!this.orgId) {
        await store.dispatch('lmsCourse/LOAD_CATALOG')
        return
      }

      // Unabhängige Daten parallel laden (statt nacheinander → deutlich schneller)
      const [, , groups] = await Promise.all([
        store.dispatch('lmsCourse/LOAD_CATALOG'),
        store.dispatch('courseAssignment/LOAD_ALL', this.orgId),
        getMyGroups(this.orgId, this.jwt).catch(() => [] as Group[]),
        store.dispatch('instruction/LOAD_ALL', { force: true }).catch(() => undefined),
        store.dispatch('instruction/LOAD_MY_CONFIRMATIONS', { force: true }).catch(() => undefined),
      ])
      this.myGroups = groups as Group[]

      // Fortschritt nur für echte Kurse laden (Unterweisungs-Zuweisungen haben kein courseId).
      // Lektionen ebenfalls laden, damit der Fortschrittsbalken auf der Karte rechnen kann.
      const courseIds = new Set<string>()
      ;(store.state as any).courseAssignment.all.forEach((a: any) => {
        if (a.kind !== 'INSTRUCTION' && a.courseId) courseIds.add(a.courseId)
      })
      await Promise.all(
        Array.from(courseIds).flatMap((moduleId) => [
          store.dispatch('lmsCourse/LOAD_PROGRESS', { userId: this.userId, moduleId }),
          store.dispatch('lmsCourse/LOAD_LESSONS', moduleId),
        ])
      )

      this.loading = false
    },
  },

  watch: {
    // Beim ersten Landen nach Login ist die Org evtl. noch nicht geladen – dann nachziehen
    ready(val: boolean) {
      if (val) this.init()
    },
  },

  async mounted() {
    await this.init()
  },
})
